import React, { useEffect } from "react";
import { Link } from "react-router-dom";

/**
 * Snapshot of the home hero as it looked TWO STEPS AGO (commit e84eeda, 2026-08-14 07:54):
 * The fully-coded "Cinematic" hero — live React components, not a flat image:
 *   • Colonnade backdrop + warm chiaroscuro gradients + film grain
 *   • Girl-on-plinth cut-out photo centred (g-3.webp)
 *   • LEFT: GIRLS kicker, big serif "Butterfly Bliss Pink", dek, ₹1,199, SIZE chips, Add to Cart, Buy it now, STORY|CRAFT|CARE tabs
 *   • RIGHT: three gold-hairline-connected macro detail circles (embroidery / fabric / lining)
 *   • Golden 360° drag-to-turn arc at the bottom
 */
const HomeCinematicPreview = () => {
  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.id = "melavi-cinematic-preview-style";
    styleEl.textContent = `
      header[data-testid="site-header"] {
        background: transparent !important;
        border-bottom: none !important;
        backdrop-filter: none !important;
        color: #F6F1E9 !important;
      }
      header[data-testid="site-header"] * { color: #F6F1E9 !important; border-color: rgba(246,241,233,0.4) !important; }
      header[data-testid="site-header"] svg { fill: #F6F1E9 !important; stroke: #F6F1E9 !important; }
    `;
    document.head.appendChild(styleEl);
    return () => { styleEl.remove(); };
  }, []);

  return (
    <div data-testid="home-cinematic-preview">
      <div
        className="fixed top-3 right-3 z-[60] px-3 py-2 text-[11px] tracking-[0.18em] uppercase"
        style={{ background: "rgba(246,241,233,0.94)", color: "#1C1A19", border: "1px solid #C9B99F" }}
      >
        Cinematic hero · pre 2026-08-14 07:59
      </div>

      {/* HERO — cinematic dark product spotlight (Butterfly Bliss Pink) */}
      <section className="relative w-full overflow-hidden" data-testid="hero" style={{ minHeight: "100vh", background: "#1c1512" }}>
        <img
          src="/api/uploads/home/colonnade.webp"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.9 }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(20,15,12,0.85) 0%, rgba(20,15,12,0.55) 30%, rgba(20,15,12,0.15) 55%, rgba(20,15,12,0.35) 100%)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,15,12,0.55) 0%, transparent 22%, transparent 70%, rgba(20,15,12,0.85) 100%)" }} />
        <div className="grain absolute inset-0 opacity-40" />

        <div className="absolute inset-0 flex items-end justify-center pointer-events-none">
          <img
            src="/api/uploads/_lifestyle/girls/g-3.webp"
            alt="Butterfly Bliss Pink on a small girl standing on a stone plinth"
            className="h-[78vh] w-auto object-contain object-bottom"
            style={{ filter: "drop-shadow(0 30px 60px rgba(0,0,0,0.55))" }}
          />
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 pt-24 md:pt-32 pb-16 min-h-[100vh] grid lg:grid-cols-12 gap-6" style={{ color: "#F6F1E9" }}>
          <div className="lg:col-span-4 flex flex-col justify-center pt-4 lg:pt-0" data-testid="hero-info">
            <div className="text-[10px] tracking-[0.32em] uppercase mb-4" style={{ color: "rgba(246,241,233,0.65)" }}>Girls</div>
            <h1 className="serif text-5xl md:text-6xl leading-[1.02]" data-testid="hero-headline" style={{ color: "#F6F1E9" }}>
              Butterfly<br />Bliss <em className="italic">Pink</em>
            </h1>
            <p className="mt-6 max-w-[36ch] text-[15px] leading-relaxed" style={{ color: "rgba(246,241,233,0.75)" }}>
              Hand-dyed pink cotton,<br />back-tie, fluttering butterflies.
            </p>
            <div className="mt-8 flex items-baseline gap-4">
              <span className="serif text-4xl" style={{ color: "#F6F1E9" }}>₹1,199</span>
              <span className="text-sm" style={{ color: "rgba(246,241,233,0.65)" }}>1–3 years</span>
            </div>
            <div className="mt-8">
              <div className="text-[10px] tracking-[0.32em] uppercase mb-3" style={{ color: "rgba(246,241,233,0.65)" }}>Size</div>
              <div className="flex gap-3">
                <Link to="/product/butterfly-bliss-pink" className="px-5 py-3 text-sm border transition-colors hover:bg-white/10" style={{ borderColor: "rgba(246,241,233,0.35)", color: "#F6F1E9" }} data-testid="hero-size-1-2y">
                  1-2Y <span className="opacity-60 ml-2 text-[10px] tracking-[0.28em] uppercase">· 6 left</span>
                </Link>
                <Link to="/product/butterfly-bliss-pink" className="px-5 py-3 text-sm border transition-colors hover:bg-white/10" style={{ borderColor: "rgba(246,241,233,0.35)", color: "#F6F1E9" }} data-testid="hero-size-2-3y">
                  2-3Y <span className="opacity-60 ml-2 text-[10px] tracking-[0.28em] uppercase">· 6 left</span>
                </Link>
              </div>
            </div>
            <div className="mt-8 flex flex-col gap-3 max-w-[280px]">
              <Link to="/product/butterfly-bliss-pink" data-testid="hero-add-to-cart" className="px-6 py-3.5 text-center text-sm tracking-[0.28em] uppercase transition-all" style={{ background: "rgba(246,241,233,0.08)", border: "1px solid rgba(246,241,233,0.35)", color: "#F6F1E9" }}>
                Add to cart
              </Link>
              <Link to="/checkout" data-testid="hero-buy-now" className="px-6 py-3.5 text-center text-sm tracking-[0.28em] uppercase transition-all hover:bg-white/10" style={{ border: "1px solid rgba(246,241,233,0.35)", color: "#F6F1E9" }}>
                Buy it now
              </Link>
            </div>
            <div className="mt-10 flex gap-6 text-[11px] tracking-[0.28em] uppercase" style={{ color: "rgba(246,241,233,0.65)" }}>
              <span className="pb-2" style={{ borderBottom: "1px solid #F6F1E9", color: "#F6F1E9" }}>The story</span>
              <span>|</span>
              <span>The craft</span>
              <span>|</span>
              <span>Care</span>
            </div>
          </div>

          <div className="lg:col-span-5"></div>

          <div className="lg:col-span-3 hidden lg:flex flex-col justify-center gap-8" data-testid="hero-macro-panel">
            {[
              { img: "/api/uploads/butterfly-bliss-pink/embroidery.webp", l1: "Hand-embroidered", l2: "Butterfly Stitches" },
              { img: "/api/uploads/butterfly-bliss-pink/fabric.webp",     l1: "Organic Cotton",    l2: "Weave" },
              { img: "/api/uploads/butterfly-bliss-pink/lining.webp",     l1: "Hand-dyed",         l2: "Tonal Variation" },
            ].map((m, i) => (
              <div key={i} className="flex items-center gap-6" data-testid={`hero-macro-${i}`}>
                <div className="relative">
                  <div className="w-32 h-32 rounded-full overflow-hidden" style={{ border: "1px solid rgba(224,180,120,0.65)" }}>
                    <img src={m.img} alt={`${m.l1} ${m.l2}`} className="w-full h-full object-cover" />
                  </div>
                  <span className="absolute right-full top-1/2 h-px w-14" style={{ background: "linear-gradient(90deg, transparent, rgba(224,180,120,0.85))" }} />
                </div>
                <div>
                  <div className="serif text-lg leading-tight" style={{ color: "#F6F1E9", textShadow: "0 2px 8px rgba(20,15,12,0.85)" }}>{m.l1}</div>
                  <div className="serif text-lg leading-tight" style={{ color: "#F6F1E9", textShadow: "0 2px 8px rgba(20,15,12,0.85)" }}>{m.l2}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-6 flex flex-col items-center gap-2 z-10 pointer-events-none">
          <svg width="360" height="14" viewBox="0 0 360 14" fill="none">
            <path d="M2 6 Q 180 22 358 6" stroke="rgba(224,180,120,0.55)" strokeWidth="1" />
            {Array.from({ length: 24 }).map((_, i) => (
              <line key={i} x1={12 + i * 15} y1="8" x2={12 + i * 15} y2="12" stroke="rgba(224,180,120,0.7)" strokeWidth="0.5" />
            ))}
          </svg>
          <span className="text-[10px] tracking-[0.32em] uppercase" style={{ color: "rgba(246,241,233,0.7)" }}>360° drag to turn</span>
        </div>
      </section>

      <div className="max-w-[900px] mx-auto px-6 md:px-12 lg:px-24 py-24">
        <div className="nav-item mb-4 text-ink/60">Snapshot</div>
        <h2 className="serif text-3xl md:text-4xl leading-tight mb-6">
          This is the <em className="italic">cinematic</em> hero — the one that lived before we swapped to the flat mockup image.
        </h2>
        <ul className="text-ink/80 leading-relaxed space-y-2 text-base list-disc pl-6">
          <li>Colonnade backdrop generated via Nano Banana + warm chiaroscuro overlays + film grain.</li>
          <li>Real photo of the girl on the plinth composited on top.</li>
          <li>All the text, size chips, Add-to-Cart, macro circles, and 360° arc were <em>live React components</em> — clickable and easily editable, not baked into an image.</li>
        </ul>
        <div className="mt-10 flex gap-6">
          <Link to="/" className="crimson-link nav-item !text-[12px]">← Back to current home</Link>
          <Link to="/home-yesterday-preview" className="crimson-link nav-item !text-[12px]">See yesterday's flat-mockup version →</Link>
        </div>
      </div>
    </div>
  );
};

export default HomeCinematicPreview;
